/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import MarkerClusters from 'highcharts/modules/marker-clusters.js';
import Xrange from 'highcharts/modules/xrange.js';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import size from 'lodash/size';
import { connect } from 'react-redux';
import { InstallationType, TerminalTypeLabel } from '../../../../../shared/entities/terminal';
import TerminalHistory from '../../../../../shared/entities/terminalHistory';
import { RootState } from '../../../../../store';
import { setAnnotations, setChartsXAxisMinMax } from '../../../../../store/terminalHistory/actions';
import { setHoveredAnnotation, setTooltipStepMap } from '../../../../../store/ui/actions';
import { HoveredPointPayload } from '../../../../../store/ui/reducer';
import { getSelectedTerminalTypeLabelBasedOnTab } from '../../../../../utils/TerminalUtilities';
import ChartCustomTimelineTooltip from '../ChartCustomComponents/ChartCustomTimelineTooltip';
import { ChartItemKeys, chartSeriesContent, ChartType } from '../ChartSeriesItems';
import ChartWrapper from './ChartWrapper';
import {
  AnnotationPoint,
  ANNOTATIONS_SERIES_KEY,
  ChartSegment,
  CLUSTER_ICON_SVG,
  CLUSTER_ICON_SVG_NO_URL,
  CLUSTER_ICON_SVG_WITH_HOVER_RING,
  getAnnotations,
  getDataFromHistory,
  getHighchartsSegments,
  getInitialChartData,
  OFFLINE_SERIES_KEY,
  ONLINE_SERIES_KEY,
  STANDBY_SERIES_KEY,
  TOOLTIP_HIDE_DELAY_MS,
} from './TimelineChartHelperFunctioners';
import { defaultChartOptions, showChartOrDataAvailableHOC } from './WithTerminalHistoryChartHOC';
import { CHART_HEIGHT } from '../ChartsContainerWrapper';
// enable the xrange chart and marker clusters for highcharts
Xrange(Highcharts);
MarkerClusters(Highcharts);

const DEFAULT_Y_VALUE = 0;
const TOP_Y_VALUE = 2;

interface TimelineChartProps extends StateProps, DispatchProps {
  index: number;
  text: string;
  id: string;
  terminalHistory: { [key: number]: TerminalHistory };
  otherTerminalHistory: { [key: number]: TerminalHistory };
}

/**
 * Chart representing the timeline of a terminal's connections and transitions.
 */
function TimelineChart({
  index,
  text,
  id,
  terminalHistory,
  otherTerminalHistory,
  chartsXMaxZoom,
  chartsXMinZoom,
  chartsXMin,
  chartsXMax,
  chartGapSeconds,
  setChartsXAxisMinMax,
  setAnnotations,
  setHoveredAnnotation,
  setTooltipStepMap,
  hoveredAnnotation,
  tooltipStepMap,
  terminalTypeLabel,
  terminalInstallationType,
  otherTerminalTypeLabel,
  selectedTerminalType,
}: TimelineChartProps) {
  const container = useRef<HTMLElement>(null);
  const [chart, setChart] = useState<Highcharts.Chart | null>(null);
  // containers the map for clustered points for which step in the stepper each cluster is currently on
  // const [tooltipStepMap, setTooltipStepMap] = useState<{ [key: number]: number }>({});
  const [showTooltip, setShowTooltip] = useState(false);
  const [pointMouseOut, setPointMouseOut] = useState(false);
  // need this stateful hovered point for the timeline in order to persist the tooltip with a timeout
  // while still hiding the "active" state of the point on the flight path
  const [timelineHoveredPoint, setTimelineHoveredPoint] = useState<Partial<AnnotationPoint> | null>(null);
  // manage the state of the annotations here and push the clustered annotations to the global store
  const [annotations, setTotalAnnotations] = useState<Partial<AnnotationPoint>[]>([]);

  const [terminalYValue, setTerminalYValue] = useState(0);
  const [otherTerminalYValue, setOtherTerminalYValue] = useState(2);

  /**
   * Create the chart using highcharts
   */
  useEffect(() => {
    if (container && container.current) {
      let chartOptions = merge(
        {
          ...defaultChartOptions(ChartType.xrange),
        },
        {
          xAxis: {
            events: {
              setExtremes: function (setExtremesEventObject: Highcharts.AxisSetExtremesEventObject) {
                setShowTooltip(false);
                setChartsXAxisMinMax(setExtremesEventObject.min, setExtremesEventObject.max);

                // @ts-ignore
                const { chart } = this;

                if (chart) {
                  // the chart zoom was reset to default
                  if (!setExtremesEventObject.min && !setExtremesEventObject.max) {
                    // @ts-ignore
                    let resetZoomButton = chart.resetZoomButton;

                    // destroy all reset zoom buttons
                    if (resetZoomButton) {
                      // @ts-ignore
                      chart.resetZoomButton = resetZoomButton.destroy();
                    }
                  }

                  const annotationSeries: Highcharts.Series = chart.get(ANNOTATIONS_SERIES_KEY) as Highcharts.Series;

                  if (annotationSeries) {
                    // @ts-ignore
                    let halo = annotationSeries.halo;

                    // destroy the halo in order to make sure it does not persist between zooms
                    // fixes: https://jira.viasat.com/browse/UP-2068
                    if (halo) {
                      // @ts-ignore
                      annotationSeries.halo = halo.destroy();
                    }
                  }
                }
              },
            },
          },
          yAxis: {
            title: {
              text: '',
            },
            categories: ['Ku', 'Ka'],
            tickPositions: [0, 1],
            tickPixelInterval: 15,
            visible: false, // TODO: turn on for ku + ka labels
            type: 'category',
            gridLineColor: 'transparent',
            gridLineWidth: 0,
            minorGridLineWidth: 0,
            lineColor: 'transparent',
            startOnTick: false,
            endOnTick: false,
            min: -0.5,
            max: 10.5,
          },
          plotOptions: {
            series: {
              stickyTracking: false,
              custom: { useDetailedLabelSymbols: true },
            },
            xrange: {
              enableMouseTracking: false,
              pointPlacement: 'on',
              pointPadding: 0,
              groupPadding: 0,
              maxPointWidth: 12, // 10px bar for timeline with 1px stroke on each side of the bar
            },
            scatter: {
              states: {
                hover: {
                  enabled: true,
                  halo: {
                    size: 18,
                  },
                },
              },
              marker: {
                enabled: true,
                states: {
                  hover: {
                    enabled: true,
                  },
                },
              },
              dataLabels: {
                enabled: true,
                pointFormat: '',
              },
              cluster: {
                enabled: true,
                animation: false,
                layoutAlgorithm: {
                  type: 'kmeans',
                  iterations: 2,
                  gridSize: 25,
                  distance: 25,
                },
                events: {
                  // modified from the original drill to cluster event to only zoom on x axis
                  drillToCluster: function (event: Highcharts.PointClickEventObject) {
                    event.preventDefault();

                    const { point } = event;
                    // @ts-ignore
                    const { clusteredData } = point;

                    if (clusteredData) {
                      let sortedDataX = clusteredData
                        .map((data: Highcharts.Point): number => data.x)
                        .sort((a: number, b: number): number => a - b);

                      let minX = sortedDataX[0];
                      let maxX = sortedDataX[sortedDataX.length - 1];

                      const offsetX = Math.abs((maxX - minX) * 0.2);

                      // Swap when minus values.
                      if (minX > maxX) {
                        [minX, maxX] = [maxX, minX];
                      }

                      // @ts-ignore
                      point.series.chart.pointer.zoomX = true;

                      // reset the pointer and tooltip to avoid an error occuring after zooming in
                      setShowTooltip(false);
                      point.series.chart.pointer.reset(false);
                      point.series.chart.tooltip.destroy();
                      // @ts-ignore
                      point.series.chart.zoom({
                        originalEvent: event,
                        xAxis: [
                          {
                            axis: point.series.xAxis,
                            min: minX - offsetX,
                            max: maxX + offsetX,
                          },
                        ],
                        yAxis: [
                          {
                            axis: point.series.yAxis,
                          },
                        ],
                      });
                    }
                  } as Highcharts.MarkerClusterDrillCallbackFunction,
                },
                dataLabels: {
                  enabled: true,
                  overflow: 'none',
                  crop: false,
                  allowOverlap: true,
                  animation: {
                    defer: 0,
                  },
                },
                marker: {
                  enabled: true,
                  width: 26,
                  height: 26,
                  symbol: CLUSTER_ICON_SVG,
                },
              },
              point: {
                events: {
                  mouseOut: () => {
                    setPointMouseOut(true);
                    setHoveredAnnotation({ annotation: null, source: 'timeline' });
                  },
                  mouseOver: function () {
                    // @ts-ignore
                    if (this.graphic) {
                      // @ts-ignore
                      this.graphic.toFront();
                    }
                    // @ts-ignore
                    if (this.series.halo) {
                      // @ts-ignore
                      this.series.halo.toFront();
                    }
                    setPointMouseOut(false);
                  },
                },
              },
            },
          },
          chart: {
            animation: false,
            height: 110,
            spacing: [0, 10, 15, 0],
            className: 'ftv__charts--timeline',
            resetZoomButton: {
              position: {
                x: 0,
                y: 0,
              },
            },
          },
        },
      );

      chartOptions.series = getInitialChartData();
      chartOptions.tooltip = {
        // @ts-ignore
        useHTML: true,
        formatter: function () {
          setTimelineHoveredPoint({ ...this.point });
          setHoveredAnnotation({ annotation: { ...this.point }, source: 'timeline' });

          return '';
        },
        outside: true, // https://api.highcharts.com/highcharts/tooltip.outside
        shadow: false,
        shared: false,
        shape: 'square',
        className: 'ftv__timeline--tooltip', // see highcharts static css to hide the default tooltip
        snap: 0,
        // @ts-ignore
        distance: 20,
        hideDelay: 99000,
        followTouchMove: false,
      };
      let chart = Highcharts.chart(container.current, chartOptions);

      setChart(chart);
    }
  }, [container, setChart, setChartsXAxisMinMax, setHoveredAnnotation]);

  useEffect(() => {
    // sync the chart zoom level
    // if the values are undefined the chart will be set back to its original state
    if (chartsXMinZoom !== null && chartsXMaxZoom !== null) {
      if (chart && chart.xAxis && chart.xAxis[0]) {
        chart.xAxis[0].setExtremes(chartsXMinZoom, chartsXMaxZoom, true, false);
      }
    }
  }, [chart, chartsXMaxZoom, chartsXMinZoom]);

  useEffect(() => {
    if (
      otherTerminalTypeLabel === TerminalTypeLabel.KU &&
      terminalTypeLabel === TerminalTypeLabel.KA &&
      terminalInstallationType === InstallationType.KU_PLUS_KA
    ) {
      setTerminalYValue(TOP_Y_VALUE);
      setOtherTerminalYValue(DEFAULT_Y_VALUE);
    } else {
      setTerminalYValue(DEFAULT_Y_VALUE);
      setOtherTerminalYValue(TOP_Y_VALUE);
    }
  }, [terminalTypeLabel, otherTerminalTypeLabel, terminalInstallationType, setTerminalYValue, setOtherTerminalYValue]);

  useEffect(() => {
    if (chart && terminalInstallationType === InstallationType.KU_PLUS_KA) {
      const standbySeries: Highcharts.Series = chart.get(STANDBY_SERIES_KEY) as Highcharts.Series;

      if (standbySeries) {
        standbySeries.update({
          showInLegend: true,
        } as Highcharts.SeriesOptionsType);
      }
    }
  }, [chart, terminalInstallationType]);

  useEffect(() => {
    // reset the data
    if (chart) {
      const onlineAndOfflineSeriesData = getDataFromHistory(terminalHistory, chartGapSeconds, chartsXMin, chartsXMax);
      const onlineSeries: Highcharts.Series = chart.get(ONLINE_SERIES_KEY) as Highcharts.Series;
      const offlineSeries: Highcharts.Series = chart.get(OFFLINE_SERIES_KEY) as Highcharts.Series;
      const standbySeries: Highcharts.Series = chart.get(STANDBY_SERIES_KEY) as Highcharts.Series;
      const isMultipleTimeline = terminalInstallationType === InstallationType.KU_PLUS_KA;

      // get the series data and update the annotations
      const onlineSeriesData = getHighchartsSegments(
        onlineAndOfflineSeriesData.ONLINE,
        terminalHistory,
        terminalYValue,
      );
      const offlineSeriesData = getHighchartsSegments(
        onlineAndOfflineSeriesData.OFFLINE,
        terminalHistory,
        terminalYValue,
      );
      const standbySeriesData = getHighchartsSegments(
        onlineAndOfflineSeriesData.STANDBY,
        terminalHistory,
        terminalYValue,
      );
      const newAnnotationSeriesData = getAnnotations(onlineAndOfflineSeriesData, terminalHistory, isMultipleTimeline);

      let otherSeriesData: { ONLINE: ChartSegment[]; OFFLINE: ChartSegment[]; STANDBY: ChartSegment[] } = {
        ONLINE: [],
        OFFLINE: [],
        STANDBY: [],
      };
      let otherOnlineSeriesData: Partial<Highcharts.Point>[] = [];
      let otherOfflineSeriesData: Partial<Highcharts.Point>[] = [];
      let otherStandbySeriesData: Partial<Highcharts.Point>[] = [];
      let otherNewAnnotationSeriesData: Partial<Highcharts.PointOptionsObject>[] = [];

      if (isMultipleTimeline) {
        otherSeriesData = getDataFromHistory(otherTerminalHistory, chartGapSeconds, chartsXMin, chartsXMax);
        otherOnlineSeriesData = getHighchartsSegments(
          otherSeriesData.ONLINE,
          otherTerminalHistory,
          otherTerminalYValue,
        );
        otherOfflineSeriesData = getHighchartsSegments(
          otherSeriesData.OFFLINE,
          otherTerminalHistory,
          otherTerminalYValue,
        );
        otherStandbySeriesData = getHighchartsSegments(
          otherSeriesData.STANDBY,
          otherTerminalHistory,
          otherTerminalYValue,
        );
        otherNewAnnotationSeriesData = getAnnotations(otherSeriesData, otherTerminalHistory, isMultipleTimeline);
      }

      setTotalAnnotations([...newAnnotationSeriesData, ...otherNewAnnotationSeriesData]);

      offlineSeries.setData(offlineSeriesData.concat(otherOfflineSeriesData), false);
      standbySeries.setData(standbySeriesData.concat(otherStandbySeriesData), false);

      // set the online series data and redraw the chart
      onlineSeries.setData(onlineSeriesData.concat(otherOnlineSeriesData), true, false);
    }
  }, [
    chart,
    terminalHistory,
    otherTerminalHistory,
    chartGapSeconds,
    setTotalAnnotations,
    terminalYValue,
    otherTerminalYValue,
    chartsXMin,
    chartsXMax,
    terminalInstallationType,
  ]);

  useEffect(() => {
    if (chart) {
      const annotationSeries: Highcharts.Series = chart.get(ANNOTATIONS_SERIES_KEY) as Highcharts.Series;

      // Wait to set the annotation until the data is loaded. Default min and max are below the minimum range
      // threshold for the time series data.
      // @ts-ignore
      if (
        chart.xAxis &&
        chart.xAxis.length &&
        chart.xAxis[0] &&
        // @ts-ignore
        chart.xAxis[0].dataMax - chart.xAxis[0].dataMin > chart.xAxis[0].minRange
      ) {
        annotationSeries.setData(annotations);

        // @ts-ignore
        if (size(annotationSeries.markerClusterSeriesData)) {
          // @ts-ignore
          setAnnotations(annotationSeries.markerClusterSeriesData.slice());
        }
      }
    }
  }, [annotations, chart, setAnnotations, chartsXMaxZoom, chartsXMinZoom]);

  useEffect(() => {
    if (chart && chart.yAxis && chart.yAxis.length) {
      const categories: string[] = new Array(TOP_Y_VALUE + 1).fill('');
      const isDoubleTimeline =
        terminalInstallationType === InstallationType.KU_PLUS_KA && !isNil(otherTerminalTypeLabel);

      const addTerminalTypeLabel = (type: TerminalTypeLabel | undefined, replaceIndex: number) => {
        switch (type) {
          case TerminalTypeLabel.CWS1:
            categories.splice(replaceIndex, 1, 'CWS1 Ant.');
            break;
          case TerminalTypeLabel.CWS2:
            categories.splice(replaceIndex, 1, 'CWS2 Ant.');
            break;
          case TerminalTypeLabel.KA:
            categories.splice(replaceIndex, 1, 'Ka Ant.');
            break;
          case TerminalTypeLabel.KU:
            categories.splice(replaceIndex, 1, 'Ku Ant.');
            break;
          case TerminalTypeLabel.KUKA:
            categories.splice(replaceIndex, 1, 'KuKa Ant.');
            break;
          case TerminalTypeLabel.MMT:
            categories.splice(replaceIndex, 1, 'MMT Ant.');
            break;
          case TerminalTypeLabel.VI:
            categories.splice(replaceIndex, 1, 'VI');
            break;
          case TerminalTypeLabel.RTE:
            categories.splice(replaceIndex, 1, 'RTE');
            break;
          default:
            break;
        }
      };

      if (
        (terminalInstallationType === InstallationType.DUAL_TERMINAL ||
          terminalInstallationType === InstallationType.HALONET_TERMINAL) &&
        selectedTerminalType !== terminalTypeLabel
      ) {
        addTerminalTypeLabel(otherTerminalTypeLabel, terminalYValue);
      } else {
        addTerminalTypeLabel(terminalTypeLabel, terminalYValue);
      }

      // only add the double timeline label for Ku + Ka
      if (isDoubleTimeline) {
        addTerminalTypeLabel(otherTerminalTypeLabel, otherTerminalYValue);
      }

      if (categories.length) {
        chart.yAxis[0].update({
          categories,
          tickPositions: isDoubleTimeline ? [0, 2] : [0],
          visible: true,
        });
      }
    }
  }, [
    chart,
    terminalTypeLabel,
    otherTerminalTypeLabel,
    terminalInstallationType,
    terminalYValue,
    otherTerminalYValue,
    selectedTerminalType,
  ]);

  useEffect(() => {
    if (chart && chart.tooltip && chart.tooltip.container && hoveredAnnotation) {
      setShowTooltip(true);
    }
  }, [chart, hoveredAnnotation, setShowTooltip]);

  // manually hide the tooltip when:
  // 1) the user leaves the tooltip
  // 2) the user leaves the point, but does not hover over the tooltip within a given amount of time
  useEffect(() => {
    const mouseLeave = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setShowTooltip(false);
    };
    let timeout: number;

    // if the user has moved from the point set a timeout to hide the tooltip
    if (pointMouseOut) {
      timeout = setTimeout(() => {
        setShowTooltip(false);
        setTimelineHoveredPoint(null);
      }, TOOLTIP_HIDE_DELAY_MS);
    }

    // clear the timeout to hide the tooltip in case the user has hovered over the tooltip
    const mouseEnter = () => {
      clearTimeout(timeout);
    };

    if (chart && chart.tooltip && chart.tooltip.container) {
      chart.tooltip.container.addEventListener('mouseleave', mouseLeave);
      chart.tooltip.container.addEventListener('mouseenter', mouseEnter);
    }

    return () => {
      if (chart && chart.tooltip && chart.tooltip.container) {
        chart.tooltip.container.removeEventListener('mouseleave', mouseLeave);
        chart.tooltip.container.removeEventListener('mouseenter', mouseEnter);
      }

      clearTimeout(timeout);
    };
  }, [chart, hoveredAnnotation, pointMouseOut]);

  useUpdateChartPointFromHoveredPoint(chart, hoveredAnnotation);

  const chartName = text;
  return (
    <ChartWrapper name={chartName} id={id} index={index} chartType={ChartItemKeys.timeline} height={CHART_HEIGHT.SMALL}>
      {React.createElement('div', { ref: container })}
      {chart &&
        chart.tooltip &&
        chart.tooltip.container &&
        hoveredAnnotation &&
        timelineHoveredPoint &&
        hoveredAnnotation.source === 'timeline' &&
        timelineHoveredPoint.x &&
        showTooltip && (
          <ChartCustomTimelineTooltip
            container={chart.tooltip.container}
            point={timelineHoveredPoint}
            date={timelineHoveredPoint.x}
            tooltipStep={tooltipStepMap[timelineHoveredPoint.x] ? tooltipStepMap[timelineHoveredPoint.x] : 0}
            onNext={() => {
              const newStep = { ...tooltipStepMap };
              if (timelineHoveredPoint && timelineHoveredPoint.x) {
                let tempStep = tooltipStepMap[timelineHoveredPoint.x];
                newStep[timelineHoveredPoint.x] = tempStep ? tempStep + 1 : 1;
              }
              setTooltipStepMap(newStep);
            }}
            onPrevious={() => {
              const newStep = { ...tooltipStepMap };
              if (timelineHoveredPoint && timelineHoveredPoint.x) {
                let tempStep = tooltipStepMap[timelineHoveredPoint.x];
                newStep[timelineHoveredPoint.x] = tempStep ? tempStep - 1 : 0;
              }
              setTooltipStepMap(newStep);
            }}
          />
        )}
    </ChartWrapper>
  );
}

const mapStateToProps = (state: RootState) => ({
  chartsXMinZoom: state.terminalHistory.chartsXAxisMinZoom,
  chartsXMaxZoom: state.terminalHistory.chartsXAxisMaxZoom,
  chartsXMin: state.terminalHistory.chartsXAxisMin,
  chartsXMax: state.terminalHistory.chartsXAxisMax,
  chartGapSeconds: state.config.appConfig.chartsConfig.timelineGapSec,
  otherTerminalHistory: state.terminalHistory.otherTerminalHistory,
  terminalTypeLabel: state.terminals.tailDetailsPanelTerminal
    ? state.terminals.tailDetailsPanelTerminal.terminalTypeLabel
    : undefined,
  terminalInstallationType: state.terminals.tailDetailsPanelTerminal
    ? state.terminals.tailDetailsPanelTerminal.installationType
    : undefined,
  otherTerminalTypeLabel:
    state.terminals.tailDetailsPanelTerminal &&
    state.terminals.tailDetailsPanelTerminal.terminalsOnAircraft &&
    state.terminals.tailDetailsPanelTerminal.terminalsOnAircraft.length
      ? state.terminals.tailDetailsPanelTerminal.terminalsOnAircraft[0].terminalTypeLabel
      : undefined,
  hoveredAnnotation: state.ui.hoveredAnnotation,
  tooltipStepMap: state.ui.tooltipStepMap,
  selectedTerminalType: getSelectedTerminalTypeLabelBasedOnTab(
    state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedTerminalTabIndex,
    state.terminals.tailDetailsPanelTerminal,
  ),
});
type StateProps = ReturnType<typeof mapStateToProps>;

const mapDispatchToProps = {
  setChartsXAxisMinMax,
  setAnnotations,
  setHoveredAnnotation,
  setTooltipStepMap,
};
type DispatchProps = typeof mapDispatchToProps;

export default showChartOrDataAvailableHOC(
  connect(mapStateToProps, mapDispatchToProps)(TimelineChart),
  chartSeriesContent[ChartItemKeys.timeline],
);

// Change the highcharts point from active to normal based on the global hovered annotation
function useUpdateChartPointFromHoveredPoint(chart: Highcharts.Chart | null, hoveredAnnotation: HoveredPointPayload) {
  useEffect(() => {
    let timelinePointToChange: Highcharts.Point | null = null;
    let clusterPointToChange: Highcharts.Point | null = null;

    // Set the point as active if hovered on the flight path.
    // This does not work for the clustered points, so handle the clustered points in a different way.
    if (
      chart &&
      hoveredAnnotation &&
      hoveredAnnotation.source === 'flightpath' &&
      hoveredAnnotation.annotation &&
      hoveredAnnotation.annotation.x &&
      !hoveredAnnotation.annotation.clusteredData
    ) {
      timelinePointToChange = chart.get(hoveredAnnotation.annotation.x.toString()) as Highcharts.Point;

      if (timelinePointToChange) {
        timelinePointToChange.setState('hover');
      }
    } else if (
      chart &&
      hoveredAnnotation &&
      hoveredAnnotation.annotation &&
      !isNil(hoveredAnnotation.annotation.clusteredData)
    ) {
      const annotationSeries: Highcharts.Series = chart.get(ANNOTATIONS_SERIES_KEY) as Highcharts.Series;

      // @ts-ignore
      if (annotationSeries.markerClusterSeriesData) {
        // find the right clustered point and manually change the dom element to reflect a hovered cluster point

        // @ts-ignore
        clusterPointToChange = annotationSeries.markerClusterSeriesData.find(
          // @ts-ignore
          data => data.x === hoveredAnnotation.annotation.x,
        );

        changeClusterPointToHover(clusterPointToChange);
      }
    }

    return () => {
      if (timelinePointToChange && timelinePointToChange.setState) {
        timelinePointToChange.setState('normal');
      }

      if (clusterPointToChange) {
        changeClusterPointToNormal(clusterPointToChange);
      }
    };
  }, [chart, hoveredAnnotation]);
}

/**
 * Change the Highcharts cluster point into an active state with the hovered icon.
 * @param point a highcharts clustered data point (Highcharts.Point with the cluster properties)
 */
const changeClusterPointToHover = (point: any) => {
  if (point.graphic && point.graphic.element && point.graphic.element.href && point.isCluster) {
    point.graphic.element.href.baseVal = CLUSTER_ICON_SVG_WITH_HOVER_RING;

    point.graphic.element.width.baseVal.newValueSpecifiedUnits(1, 40);
    point.graphic.element.height.baseVal.newValueSpecifiedUnits(1, 40);

    const transform = point.graphic.element.transform.baseVal.getItem(0);
    transform.setTranslate(-20, -20);
  }
};

/**
 * Change the Highcharts cluster point to use the original non-active icon.
 * @param point a highcharts clustered data point (Highcharts.Point with the cluster properties)
 */
const changeClusterPointToNormal = (point: any) => {
  if (point.graphic && point.graphic.element && point.graphic.element.href && point.isCluster) {
    point.graphic.element.href.baseVal = CLUSTER_ICON_SVG_NO_URL;

    point.graphic.element.width.baseVal.newValueSpecifiedUnits(1, 26);
    point.graphic.element.height.baseVal.newValueSpecifiedUnits(1, 26);

    const transform = point.graphic.element.transform.baseVal.getItem(0);
    transform.setTranslate(-13, -13);
  }
};
